'use client';

import { c as _c } from "react/compiler-runtime";
import type { PublicWikiData, WikiPage } from '@/types/wiki-content';
import { ArrowLeft, BookOpen, ExternalLink } from 'lucide-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { PublicResourcesNav } from '@/components/shared/public-resources-nav';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { WikiContentDisplay } from '@/components/wiki/wiki-content-display';
import { useWikiPageNavigation } from '@/hooks/use-wiki-page-navigation';
type PublicWikiContentProps = {
  initialData: PublicWikiData;
  owner: string;
  repo: string;
};
export function PublicWikiContent(t0) {
  const $ = _c(35);
  const {
    initialData,
    owner,
    repo
  } = t0;
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("page") ?? undefined;
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialPage = t1;
  const {
    selectedPageRef,
    handlePageSelect,
    setSelectedPageRef
  } = useWikiPageNavigation(owner, repo, true);
  let t2;
  bb0: {
    if (!initialData?.wiki?.content?.wiki_structure?.pages) {
      let t3;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = [];
        $[2] = t3;
      } else {
        t3 = $[2];
      }
      t2 = t3;
      break bb0;
    }
    const sourcePages = initialData.wiki.content.wiki_structure.pages;
    if (!Array.isArray(sourcePages)) {
      let t3;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = [];
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      t2 = t3;
      break bb0;
    }
    let t3;
    if ($[4] !== sourcePages) {
      t3 = sourcePages.map(_temp);
      $[4] = sourcePages;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = t3;
  }
  const pages = t2;
  let wikiStructure = null;
  let wikiData = null;
  if (initialData?.wiki?.content) {
    const content = initialData.wiki.content;
    if (content.wiki_structure?.pages && Array.isArray(content.wiki_structure.pages)) {
      wikiStructure = content.wiki_structure;
      const t3 = initialData.wiki.source_commit_sha ?? content.source_commit_sha;
      const t4 = initialData.wiki.source_branch ?? content.source_branch;
      let t5;
      if ($[6] !== content || $[7] !== t3 || $[8] !== t4) {
        t5 = {
          ...content,
          source_commit_sha: t3,
          source_branch: t4
        };
        $[6] = content;
        $[7] = t3;
        $[8] = t4;
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      wikiData = t5;
    }
  }
  let t3;
  if ($[10] !== initialPage || $[11] !== pages || $[12] !== selectedPageRef || $[13] !== setSelectedPageRef) {
    t3 = () => {
      if (!selectedPageRef && pages.length > 0) {
        const pageToSelect = pages.find(p => p.pageRef === initialPage)?.pageRef || pages[0]?.pageRef || "";
        setSelectedPageRef(pageToSelect, {
          replace: true
        });
      }
    };
    $[10] = initialPage;
    $[11] = pages;
    $[12] = selectedPageRef;
    $[13] = setSelectedPageRef;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== initialPage || $[16] !== pages || $[17] !== selectedPageRef) {
    t4 = [pages, selectedPageRef, initialPage];
    $[15] = initialPage;
    $[16] = pages;
    $[17] = selectedPageRef;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  useEffect(t3, t4);
  if (pages.length === 0) {
    let t5;
    let t6;
    let t7;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <BookOpen className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />;
      t6 = <h2 className="mb-2 text-2xl font-semibold">Wiki Not Available</h2>;
      t7 = <p className="mb-6 text-muted-foreground">This repository doesn't have a public wiki yet.</p>;
      $[19] = t5;
      $[20] = t6;
      $[21] = t7;
    } else {
      t5 = $[19];
      t6 = $[20];
      t7 = $[21];
    }
    let t8;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Button variant="outline" asChild={true}><Link href="/wikis"><ArrowLeft className="mr-2 h-4 w-4" />Browse other wikis</Link></Button>;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    const t9 = `https://github.com/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}`;
    let t10;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <ExternalLink className="ml-2 h-4 w-4" />;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    let t11;
    if ($[24] !== t9) {
      t11 = <div className="container mx-auto px-4 py-16"><Card className="p-12 text-center">{t5}{t6}{t7}<div className="flex justify-center gap-4">{t8}<Button variant="default" asChild={true}><a href={t9} target="_blank" rel="noopener noreferrer">View on GitHub{t10}</a></Button></div></Card></div>;
      $[24] = t9;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    return t11;
  }
  let t5;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PublicResourcesNav current="/wikis" className="mt-16 px-0 pb-0 md:px-0" />;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== handlePageSelect || $[28] !== owner || $[29] !== pages || $[30] !== repo || $[31] !== selectedPageRef || $[32] !== wikiData || $[33] !== wikiStructure) {
    t6 = <div className="relative h-full"><WikiContentDisplay pages={pages} selectedPageRef={selectedPageRef} setSelectedPageRef={handlePageSelect} owner={owner} repoName={repo} wikiData={wikiData} wikiStructure={wikiStructure} isPublicView={true} afterContent={t5} /></div>;
    $[27] = handlePageSelect;
    $[28] = owner;
    $[29] = pages;
    $[30] = repo;
    $[31] = selectedPageRef;
    $[32] = wikiData;
    $[33] = wikiStructure;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  return t6;
}
function _temp(page, index) {
  const pageRef = page.pageRef || page.id || page.filePath?.replace(/[^\w-]/g, "-") || page.title?.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "") || `page-${index + 1}`;
  return {
    id: page.id || pageRef,
    pageRef,
    title: page.title || "Untitled",
    content: page.content || "",
    filePath: page.filePath || "",
    generatedTimestamp: new Date().toISOString(),
    htmlContent: page.htmlContent
  };
}